import { FEATURE_TYPES } from "../constants";
import * as routes from "../routes";
import {
	anyOf,
	globs
} from "./globs";
import {
	ActionOrPattern,
	Any,
	Pattern
} from "./security/actionScopes";
import { isAnyTypeWritePermittedScope } from "./security/commonScopes";
import { PROXY_TYPES } from "./proxyService";

export function buildNavigation(form, permissions, securityService, proxyService) {
	const navigation = { primary: [], administration: [], administrationTitle: "Administration" };
	if(!form)
		return navigation;

	const positiveReport = form.nomenclature.PositiveReport;
	const negativeReport = form.nomenclature.NegativeReport;
	const agency = form.Agency.Name;
	const canCreate = !permissions.Reporting.IsDisabled && permissions.Reporting.Actions.CanCreate;
	const canReadCompliance = securityService.isActionAllowed(globs.reporting.compliance.read);
	const canCardSwipe = form.Features?.some(f => f.Type === FEATURE_TYPES.webCardSwipe || f.Type === FEATURE_TYPES.tokenScanToReport);

	navigation.primary = [
		{
			id: "compliance", route: "authenticated.compliance", label: "Reporting Status", icon: "time",
			description: `Here you can file a ${negativeReport} Report as well as see the status of your reporting requirement.`,
			visible: !permissions.Compliance.IsDisabled && canReadCompliance
		},
		{
			id: "create-report", route: routes.NEW_REPORT, label: `Create ${positiveReport}`, icon: "plus",
			description: `Here you can create & complete a new ${positiveReport} to the ${agency}.`, visible: canCreate
		},
		{
			id: "negative-report", route: "authenticated.compliance", label: `Create ${negativeReport}`, icon: "block",
			description: `Create a new ${negativeReport} to the ${agency}.`,
			visible: !permissions.Reporting.IsDisabled && permissions.Reporting.Actions.CanFileNegative && !permissions.Compliance.IsDisabled
		},
		{
			id: "find-report", route: "authenticated.report-list", label: `Find ${positiveReport}`, icon: "search",
			description: `Here you can see a list of previous ${positiveReport}s.`, visible: !permissions.History.IsDisabled
		},
		{
			id: "card-swipe", action: "card-swipe", label: `Create ${positiveReport} with Card`, icon: "card",
			description: `Click to begin a ${positiveReport} via card swipe.`, visible: canCardSwipe, hideOnMobile: true
		}
	].filter(item => item.visible);

	const analyticsScope = new Any([
		new Pattern(anyOf(globs.proxies.byType("ANALYTICS").self)),
		new Pattern(anyOf(globs.proxies.byType("QUERY").self))
	]);
	const exportsScope = new Any([
		new ActionOrPattern(globs.exports("*").logs("*").read),
		new ActionOrPattern(globs.exports("*").read),
		new ActionOrPattern(globs.exports("*").create)
	]);
	const noticesScope = new Any([
		new ActionOrPattern(globs.notices.create),
		new ActionOrPattern(globs.notices.update)
	]);
	const hasProxies = types => proxyService.getProxiesByType(types).length > 0;
	const administration = permissions.Administration;

	navigation.administration = [
		{
			id: "entities", route: routes.ENTITIES_SCREEN, label: "Entities", icon: "card", description: "Manage entities.",
			visible: (!administration.IsDisabled && !administration.Entity.IsDisabled)
				|| securityService.isActionScopeAllowed(isAnyTypeWritePermittedScope())
		},
		{
			id: "analytics", route: routes.ANALYTICS_SCREEN, label: "Analytics", icon: "chart", description: "Run queries and view analytics.",
			visible: hasProxies([PROXY_TYPES.query, PROXY_TYPES.system]) && securityService.isActionScopeAllowed(analyticsScope)
		},
		{
			id: "operations", route: routes.OPERATIONS_SCREEN, label: "Operations", icon: "functions", description: "Run operations.",
			visible: hasProxies([PROXY_TYPES.operation, PROXY_TYPES.system])
				&& securityService.isPatternAllowed(anyOf(globs.proxies.byType("OPERATIONS").self))
		},
		{
			id: "reporting", route: routes.REPORTING_SCREEN, label: "Reporting", icon: "file-text", description: "Generate a summary report.",
			visible: hasProxies([PROXY_TYPES.report]) && securityService.isPatternAllowed(anyOf(globs.proxies.byType("REPORTING").self))
		},
		{
			id: "exports", route: routes.EXPORTS_SCREEN, label: "Exports", icon: "archive", description: "Manage exports and view export logs.",
			visible: securityService.isActionScopeAllowed(exportsScope)
		},
		{
			id: "notices", route: routes.NOTICES_SCREEN, label: "Notices", icon: "notifications", description: "Create and manage notices.",
			visible: securityService.isActionScopeAllowed(noticesScope)
		},
		{
			id: "users", route: routes.USERS_SCREEN, label: "Users", icon: "accounts", description: "Manage users and their access.",
			visible: !administration.IsDisabled && !administration.User.IsDisabled
		}
	].filter(item => item.visible);

	return navigation;
}

function NavigationService(appState, securityService, proxyService) {
	this.getNavigation = () => buildNavigation(appState.forms.selected, appState.permissions, securityService, proxyService);
}

export default angular
	.module("vesl.services.navigation-service", [])
	.service("navigationService", NavigationService).name;
